import React, { useState, useEffect, memo } from "react";
import styled, { keyframes, css } from "styled-components";
import { Checkbox, Modal } from "@material-ui/core";
import theme from "../../../assets/theme";
import COLORS from "../../../assets/Colors";
import { useSnackbar } from "notistack";
import useLoader from "../../../hooks/useLoader";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../common/Buttons";
import { getErrorMessage } from "../../../helpers/functions";
import { CustomInputField } from "../../common/FormInputs";
import BundleService from "../../../services/BundleService";

const fadeIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -48%) scale(0.97); }
  to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

const Index = ({
  id,
  addpage,
  setAddpage,
  title,
  docName,
  setRefreshDetails,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();
  const [selectedArray, setSelectedArray] = useState(null);

  useEffect(() => {
    let initialArray = [];
    if (docName) {
      docName.map((item) => {
        let newObj = { ...item };
        newObj.checked = false;
        initialArray.push(newObj);
        return initialArray;
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docName]);

  const updateBundleDoc = async () => {
    try {
      setLoader({ state: true, message: "Add section..." });
      const payload = {
        bundleName: title,
        bundleDocuments: [{ ...selectedArray }],
      };
      const res = await BundleService.addSectionOrPagesToBundle(id, payload);
      if (res) {
        enqueueSnackbar("Successfully added section to Bundle", {
          variant: "success",
        });
        setRefreshDetails(true);
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoader({ state: false });
      setAddpage(false);
      setSelectedArray(null);
    }
  };

  const handleSelect = (item, event) => {
    if (event.target.checked) {
      let newObj = { ...item };
      newObj.checked = true;
      setSelectedArray(newObj);
    } else {
      setSelectedArray(null);
    }
  };

  const handleClose = () => {
    setAddpage(false);
    setSelectedArray(null);
  };

  return (
    <Modal open={addpage} onClose={handleClose} disableRestoreFocus>
      <DrawerContainer role="presentation">
        <Container>
          {/* Header */}
          <ModalHeader>
            <HeaderLeft>
              <Heading>Add Section</Heading>
            </HeaderLeft>
            <CloseBtn onClick={handleClose} aria-label="Close">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </CloseBtn>
          </ModalHeader>

          {/* Subtitle */}
          {title && (
            <SubTitle>
              Bundle: <BundleName>{title}</BundleName>
            </SubTitle>
          )}

          <Divider />

          {/* Doc list */}
          <ScrollArea hasMany={docName?.length > 3}>
            {docName && docName.length > 0 ? (
              docName.map((item, index) => {
                const isSelected =
                  selectedArray && selectedArray.id === item.id;
                return (
                  <DocCard key={item.name + index} selected={isSelected}>
                    <DocCardTop>
                      <DocCheckRow>
                        <CustomCheckbox
                          onChange={(ev) => handleSelect(item, ev)}
                          checked={isSelected || false}
                          name={item?.name + index}
                        />
                        <DocName selected={isSelected}>{item.name}</DocName>
                      </DocCheckRow>
                      {isSelected && <SelectedBadge>Selected</SelectedBadge>}
                    </DocCardTop>

                    <FieldsRow>
                      <FieldGroup className="bundle-doc">
                        <FieldLabel>Page No.</FieldLabel>
                        <CustomInputField
                          className="input-white"
                          variant="outlined"
                          value={
                            isSelected ? selectedArray.pageNo : item?.pageNo
                          }
                          disabled={!isSelected}
                          onChange={(event) =>
                            setSelectedArray({
                              ...selectedArray,
                              pageNo: event.target.value,
                            })
                          }
                        />
                      </FieldGroup>
                      <FieldGroup className="bundle-doc">
                        <FieldLabel>Position</FieldLabel>
                        <CustomInputField
                          className="input-white"
                          variant="outlined"
                          type="number"
                          disabled={!isSelected}
                          value={isSelected ? selectedArray.position : ""}
                          onChange={(event) =>
                            setSelectedArray({
                              ...selectedArray,
                              position: event.target.value,
                            })
                          }
                        />
                      </FieldGroup>
                    </FieldsRow>
                  </DocCard>
                );
              })
            ) : (
              <EmptyState>
                <svg
                  width="36"
                  height="36"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#acb1c2"
                  strokeWidth="1.5"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <EmptyText>No documents available</EmptyText>
              </EmptyState>
            )}
          </ScrollArea>

          <Divider />

          {/* Footer */}
          <FooterContainer>
            {selectedArray && (
              <SelectionInfo>
                <DotIndicator />1 document selected
              </SelectionInfo>
            )}
            <ButtonGroup>
              <PrimaryOutlinedCloseCTAButton
                style={{ flex: 1, maxWidth: 160 }}
                onClick={handleClose}
              >
                Cancel
              </PrimaryOutlinedCloseCTAButton>
              <PrimaryCTAButton
                style={{ flex: 1, maxWidth: 160 }}
                disabled={selectedArray === null}
                onClick={updateBundleDoc}
              >
                Submit
              </PrimaryCTAButton>
            </ButtonGroup>
          </FooterContainer>
        </Container>
      </DrawerContainer>
    </Modal>
  );
};

export default memo(Index);

export const DrawerContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 32px);
  max-width: 560px;
  outline: none;
  animation: ${fadeIn} 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);

  @media (max-width: 480px) {
    width: calc(100% - 24px);
    max-height: 95vh;
  }
`;

export const Container = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow:
    0 24px 64px rgba(41, 52, 97, 0.18),
    0 4px 16px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;

  @media (max-width: 480px) {
    border-radius: 12px;
    max-height: 95vh;
  }
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;

  @media (max-width: 480px) {
    padding: 16px 16px 12px;
  }
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const Heading = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 800;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontExtraBold};
  letter-spacing: -0.2px;
`;

export const CloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #acb1c2;
  transition:
    background 0.15s ease,
    color 0.15s ease;
  outline: none;

  &:hover {
    background: #f5f6fa;
    color: ${COLORS.COLOR_DARK};
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px rgba(41, 52, 97, 0.3);
  }
`;

export const SubTitle = styled.div`
  padding: 0 24px 14px;
  font-size: 12px;
  font-family: ${theme.fonts.primaryFontRegular};
  color: #8b91a8;

  @media (max-width: 480px) {
    padding: 0 16px 12px;
  }
`;

export const BundleName = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #293461;
`;

export const Divider = styled.div`
  height: 1px;
  background: #f0f1f7;
  margin: 0;
`;

export const ScrollArea = styled.div`
  padding: 16px 24px;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  ${({ hasMany }) =>
    hasMany &&
    css`
      max-height: 340px;
    `}

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #e1e3ee;
    border-radius: 2px;
  }

  @media (max-width: 480px) {
    padding: 12px 16px;
  }
`;

export const DocCard = styled.div`
  border-radius: 10px;
  border: 1.5px solid ${({ selected }) => (selected ? "#293461" : "#e1e3ee")};
  background: ${({ selected }) => (selected ? "rgba(41,52,97,0.03)" : "#fff")};
  padding: 12px 14px;
  margin-bottom: 10px;
  transition:
    border-color 0.18s ease,
    background 0.18s ease,
    box-shadow 0.18s ease;
  box-shadow: ${({ selected }) =>
    selected ? "0 0 0 3px rgba(41,52,97,0.08)" : "none"};

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    border-color: ${({ selected }) => (selected ? "#293461" : "#c5c9db")};
  }
`;

export const DocCardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

export const DocCheckRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

export const DocName = styled.span`
  font-size: 14px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${({ selected }) => (selected ? COLORS.COLOR_DARK : "#4a5068")};
  transition: color 0.15s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SelectedBadge = styled.div`
  font-size: 10px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #293461;
  background: rgba(41, 52, 97, 0.1);
  border-radius: 20px;
  padding: 2px 10px;
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: 8px;
  letter-spacing: 0.3px;
`;

export const FieldsRow = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 360px) {
    flex-direction: column;
    gap: 8px;
  }
`;

export const FieldGroup = styled.div`
  flex: 1;
  min-width: 0;
`;

export const FieldLabel = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.6px;
  color: #8b91a8;
  margin-bottom: 5px;
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 10px;
`;

export const EmptyText = styled.p`
  margin: 0;
  font-size: 14px;
  font-family: ${theme.fonts.primaryFontRegular};
  color: #acb1c2;
`;

export const FooterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px 20px;
  gap: 12px;

  @media (max-width: 480px) {
    padding: 14px 16px 18px;
    flex-wrap: wrap;
  }
`;

export const SelectionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #8b91a8;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

export const DotIndicator = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #293461;
  flex-shrink: 0;
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  flex: 1;
  justify-content: flex-end;

  @media (max-width: 480px) {
    width: 100%;
    justify-content: stretch;

    > button {
      flex: 1;
      max-width: none !important;
    }
  }
`;

const CustomCheckbox = ({ ...props }) => (
  <Checkbox
    color="primary"
    icon={
      <img src={require("../../../assets/images/checkbox.svg")} alt="check" />
    }
    checkedIcon={
      <img
        src={require("../../../assets/images/checkBoxSelected.svg")}
        alt="check"
      />
    }
    style={{ padding: 6 }}
    {...props}
  />
);

export const Label = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 10px;
  text-transform: capitalize;
  font-weight: 600;
  line-height: 1.7;
  margin-bottom: 6px;
  color: ${COLORS.COLOR_DARK};
  text-align: left;
  & span {
    padding: 0px;
  }
`;
